import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { gmailInviteService, INVITE_TOKEN_KEY } from '../services/gmailInviteService';

import { InvitePeopleModal } from './InvitePeopleModal';

const RETURN_PARAMS = ['gmail_invites', 'gmail_invites_error'];

const takeToken = (): string | null => {
  try {
    const token = localStorage.getItem(INVITE_TOKEN_KEY);

    localStorage.removeItem(INVITE_TOKEN_KEY);

    return token;
  } catch {
    // Blocked storage: the email match still counts the signup.
    return null;
  }
};

/** Google lands back with a full page load, so the parameters are read once, when the app starts. */
const readReturn = () => {
  const params = new URLSearchParams(window.location.search);

  return { open: RETURN_PARAMS.some((name) => params.has(name)), notice: params.get('gmail_invites_error') };
};

const useStripReturnParams = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (!RETURN_PARAMS.some((name) => params.has(name))) return;
    RETURN_PARAMS.forEach((name) => params.delete(name));
    const search = params.toString() ? `?${params.toString()}` : '';

    navigate({ pathname: location.pathname, search, hash: location.hash }, { replace: true });
  }, [location.search, location.pathname, location.hash, navigate]);
};

/** Count the signup that followed an invite's link, once the new account is signed in. */
const useInviteClaim = (signedIn: boolean) => {
  useEffect(() => {
    const token = signedIn ? takeToken() : null;

    if (token) void gmailInviteService.claim(token).catch(() => undefined);
  }, [signedIn]);
};

const projectFromPath = (): string | undefined => window.location.pathname.match(/^\/planning\/([^/?#]+)/)?.[1];

/**
 * Back from Google's consent page (``?gmail_invites=<batch>`` or ``?gmail_invites_error=``): reopen Invite people,
 * where the contacts are ready to choose from.
 */
export const GmailInviteReturn: React.FC = () => {
  const { user } = useAuth();
  const [{ open, notice }, setReturn] = useState(readReturn);

  useStripReturnParams();
  useInviteClaim(Boolean(user));

  return (
    <InvitePeopleModal
      isOpen={open}
      notice={notice}
      onClose={() => setReturn({ open: false, notice: null })}
      projectId={projectFromPath()}
    />
  );
};
